import { useMemo } from 'react';
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import { useTenant } from '@/contexts/TenantContext';
import { useMembershipsQuery } from '@/lib/api/memberships';

/**
 * Known org-name overrides — used when Core doesn't surface a friendly
 * name for an org the user can switch to. Core's `/organizations/me` +
 * `/organizations/me/subtree` don't return human-readable names against
 * an X-API-Key (the user-JWT path is also limited), so we fall back to
 * this static map for the well-known infrastructure orgs.
 *
 * Roadmap: once Core ships a public name-by-id lookup (or returns
 * names on the subtree response uniformly), drop this map and source
 * names dynamically.
 */
const KNOWN_ORG_NAMES: Record<string, { name: string; slug: string }> = {
  '5e7d0000-0000-0000-0000-000000000d40': {
    name: 'Internal Demo Org',
    slug: 'internal-demo',
  },
  // Sustevo HQ — every @sustevo.com employee's home org. Sourced from
  // Core's tenant_organizations row for the SustEvo Azure tenant.
  'e75c8670-a3fd-4a4a-92d1-2ac5ae6de46d': {
    name: 'Sustevo',
    slug: 'sustevo',
  },
};

/**
 * W16-F2-S1 — Accessible partners API.
 *
 * Backed by Core's org-subtree endpoint via the apps/api proxy
 * (`/api/v1/proxy/core/organizations/me/subtree`). The proxy already
 * scopes to the caller's accessible subtree; we filter to `tier === 'partner'`
 * so the topbar switcher only lists partner-tier nodes.
 *
 * Response is a flat list — Core's recursive-CTE result. We map each
 * partner row to a switcher-friendly shape.
 */

export interface AccessiblePartner {
  id: string;
  slug: string;
  name: string;
  logoUrl?: string;
}

interface OrgSubtreeRow {
  id: string;
  parent_id: string | null;
  tier: 'platform' | 'partner' | 'customer';
  name: string;
  slug?: string;
  logo_url?: string;
}

const PARTNERS_KEY = ['partners', 'accessible'] as const;

export function useAccessiblePartners(): UseQueryResult<AccessiblePartner[]> {
  const { isAuthenticated, getAccessToken } = useAuth();
  return useQuery({
    queryKey: PARTNERS_KEY,
    queryFn: async () => {
      const rows = await apiFetch<OrgSubtreeRow[]>(
        '/api/v1/proxy/core/organizations/me/subtree',
        { getAccessToken },
      );
      return rows
        .filter((row) => row.tier === 'partner')
        .map<AccessiblePartner>((row) => ({
          id: row.id,
          slug: row.slug ?? row.id,
          name: row.name,
          ...(row.logo_url ? { logoUrl: row.logo_url } : {}),
        }));
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Returns the active partner from the list (or `null` when not yet
 * resolved). Falls back to the first listed partner when the user has
 * no active selection — keeps the topbar showing a real name on first
 * load.
 */
export function useActivePartner(): {
  active: AccessiblePartner | null;
  partners: AccessiblePartner[];
  isLoading: boolean;
  isError: boolean;
} {
  const { activePartnerId } = useTenant();
  const list = useAccessiblePartners();
  // Memberships + homeOrgId are the authoritative source per W100-F2-S1.
  // Core's /users/me carries the home org id + every entity-scoped
  // grant. The /organizations/me/subtree call is a nice-to-have for
  // friendly names of orgs the user can SEE in their subtree, but
  // 2026-05-24 diagnosis: Core returns `org_tier='customer'` for
  // Sustevo HQ even though the user is a member of it, and the
  // previous tier='partner' filter in useAccessiblePartners drops it
  // → the switcher hides for every customer-tier home org user.
  //
  // The right shape: switchable orgs = homeOrgId ∪ distinct(memberships
  // .organization_id), regardless of Core's tier label. Subtree feeds
  // names where available; memberships + homeOrgId guarantees every
  // accessible org gets a row.
  const memberships = useMembershipsQuery();
  const homeOrgId = memberships.data?.homeOrgId;

  const partners = useMemo<AccessiblePartner[]>(() => {
    const fromSubtree = list.data ?? [];
    // Index subtree rows by id so we can look up friendly names.
    const subtreeById = new Map(fromSubtree.map((p) => [p.id, p]));
    const seen = new Set<string>();
    const out: AccessiblePartner[] = [];

    function addOrg(id: string): void {
      if (!id || seen.has(id)) return;
      seen.add(id);
      const named = subtreeById.get(id);
      const known = KNOWN_ORG_NAMES[id];
      out.push({
        id,
        slug: named?.slug ?? known?.slug ?? id.slice(0, 8),
        name: named?.name ?? known?.name ?? `Org ${id.slice(0, 8)}`,
        ...(named?.logoUrl ? { logoUrl: named.logoUrl } : {}),
      });
    }

    // 1. Home org first — always switchable, always the default.
    if (homeOrgId) addOrg(homeOrgId);
    // 2. Every partner_relationship membership.
    for (const m of memberships.data?.memberships ?? []) {
      if (m.entity_type !== 'partner_relationship') continue;
      addOrg(m.organization_id);
    }
    // 3. Any other partner-tier orgs from subtree (legacy support —
    //    if Core ever surfaces additional partner orgs the user can
    //    switch to that aren't in their memberships).
    for (const p of fromSubtree) addOrg(p.id);

    return out;
  }, [list.data, memberships.data, homeOrgId]);

  const active = useMemo<AccessiblePartner | null>(() => {
    if (partners.length === 0) return null;
    if (activePartnerId) {
      const match = partners.find((p) => p.id === activePartnerId);
      if (match) return match;
    }
    return partners[0] ?? null;
  }, [partners, activePartnerId]);

  return {
    active,
    partners,
    isLoading: list.isLoading || memberships.isLoading,
    isError: list.isError && memberships.isError,
  };
}

/**
 * Caller hook: switch active partner + invalidate every "scoped"
 * react-query namespace so asset / portfolio / module queries refetch
 * with the new tenant scope.
 *
 * @deprecated Prefer `useSwitchOrg` (apps/web/src/hooks/useSwitchOrg.ts).
 * That hook does a full `queryClient.clear()` instead of the predicate-
 * filtered invalidate below — a single missed namespace prefix here is
 * enough to leak cross-org data on first paint. New call sites should
 * use `useSwitchOrg`; this hook stays exported for the AppShell.spec
 * mock + any caller that genuinely wants the lighter-touch invalidate.
 */
export function useSwitchPartner(): (partnerId: string) => void {
  const { setActivePartner } = useTenant();
  const queryClient = useQueryClient();
  return (partnerId: string) => {
    setActivePartner(partnerId);
    // Invalidate every namespace that depends on the active partner.
    // We use predicate-based invalidation so future query keys with
    // partner scope automatically participate.
    void queryClient.invalidateQueries({
      predicate: (q) => {
        const key0 = q.queryKey[0];
        if (typeof key0 !== 'string') return false;
        return [
          'buildings',
          'portfolios',
          'org-subtree',
          'orgs.current',
          'analytics',
          'modules',
          'evaluations',
          'kpis',
          'feature-flags',
        ].some((prefix) => key0 === prefix || key0.startsWith(`${prefix}.`));
      },
    });
  };
}
